/**
 * The two rules a custom-emoji upload has to follow, in one place.
 *
 * There are two forms that add an emoji — the compact one inside the picker
 * and the full one on the group's admin page — and they had drifted. Both
 * carried the same slugifier, character for character. Only the admin one
 * checked the file, so the picker would happily upload a 4 MB photo or a PDF
 * and only find out when the server refused it.
 *
 * The FORMS are deliberately still two components: one is a popover with a
 * single row, the other a page section with an availability check and a list
 * beneath it. Forcing one layout into both containers buys a mode switch, not
 * a shared component. What was actually duplicated is here.
 */

/**
 * The ceiling `POST /groups/{ref}/emojis` enforces on the mounted upload
 * (`backend/src/routes/v1/emojis.ts`, `requireUsableEmojiUpload`). An emoji is
 * drawn at 16–28px; the generic image cap is for photographs.
 */
export const MAX_EMOJI_BYTES = 256 * 1024;

/**
 * Strips what a shortcode may not contain, as the user types it. Agrees with
 * the server's `CUSTOM_SHORTCODE_RE` — `-` is allowed there and was refused by
 * the two forms' own copies of this. Length and the two-character minimum are
 * left to validation, where the message says so.
 */
export function sanitizeEmojiName(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9_-]/g, "");
}

/**
 * A filename turned into a shortcode the server will accept: lowercase, ascii,
 * `_` for whatever else, no leading or trailing ones, at most 50 characters
 * and at least two. Falls back to `emoji` when a name reduces to less — a file
 * called `😀.png` otherwise produced the empty string, and an empty shortcode
 * fails validation with a message about the wrong thing.
 */
export function slugifyEmojiName(filename: string): string {
  const slug = filename
    .replace(/\.[^.]+$/, "")
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "_")
    .replace(/_+/g, "_")
    .slice(0, 50)
    .replace(/^[_-]+|[_-]+$/g, "");
  return slug.length >= 2 ? slug : "emoji";
}

/**
 * Why this file cannot be an emoji, in Danish, or null if it can.
 *
 * Checked before the upload rather than after: the server refuses these when
 * the emoji is created, but a round trip to be told the picture was too big is a worse way to find
 * out than not sending it.
 */
export function emojiFileError(file: File): string | null {
  if (!file.type.startsWith("image/")) {
    return "Kun billedfiler er tilladt (PNG, JPG, GIF, WebP).";
  }
  if (file.size > MAX_EMOJI_BYTES) {
    return "Billedet er for stort. Maks 256 KB.";
  }
  return null;
}
